import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useSelector } from 'react-redux'
import Tooltip from 'components/generic/tooltip'
import getLinkCell from 'components/generic/datagrid/cells/LinkCell'
import { formatNumber } from 'utils/helpers'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'
import DataGridWithSearch from '../DataGridWithSearch'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import { SORTABLE_OBT_FIELDS } from 'utils/constants'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const getColumns = (
  defaultYardTimezone = null,
  defaultYardUm,
  useMinimalObt,
  useBackendSortAndFilter,
  renderHeaderWithSort,
) => {
  const columns = [
    {
      field: 'ticketId',
      headerName: 'Ticket #',
      flex: 1,
      renderCell: params => getLinkCell(params, 'ticketId'),
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Ticket #', SORTABLE_OBT_FIELDS.TICKET_ID, { startAscending: false })
        : undefined,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'ticketStatus',
      headerName: 'Status',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'ticketEffectiveDate',
      headerName: useMinimalObt ? 'Date In' : 'Effective Date',
      type: 'date',
      headerAlign: 'left',
      align: 'left',
      flex: 1.5,
      renderCell: params => {
        if (!params.row.ticketEffectiveDate) return '--'
        return dateTimeFormatter(params.row.ticketEffectiveDate, { timeZone: defaultYardTimezone })
      },
      renderHeader: useBackendSortAndFilter
        ? () =>
            renderHeaderWithSort(useMinimalObt ? 'Date In' : 'Effective Date', SORTABLE_OBT_FIELDS.EFFECTIVE_DATE, {
              startAscending: false,
            })
        : undefined,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'ticketShipDate',
      headerName: 'Date Out',
      type: 'date',
      headerAlign: 'left',
      align: 'left',
      flex: 1.5,
      renderCell: params => {
        if (!params.row.ticketShipDate) return '--'
        return dateTimeFormatter(params.row.ticketShipDate, { timeZone: defaultYardTimezone })
      },
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'customerInfo',
      headerName: 'Consumer / Shipping Info',
      width: 250,
      renderCell: params => (
        <div>
          <Typography>{params.row.customerInfo?.name}</Typography>
          <Typography variant="subtitle2">{params.row.customerInfo?.address}</Typography>
        </div>
      ),
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Consumer / Shipping Info', SORTABLE_OBT_FIELDS.CUSTOMER_DISPLAY_NAME)
        : undefined,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'salesOrder',
      headerName: 'Sales Order #',
      flex: 1,
      renderHeader: useBackendSortAndFilter
        ? () =>
            renderHeaderWithSort('Sales Order #', SORTABLE_OBT_FIELDS.SALES_ORDER_TICKET_ID, { startAscending: false })
        : undefined,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'custOrder',
      headerName: 'Cust Order #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'truckNumber',
      headerName: 'Truck #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'trailerNumber',
      headerName: 'Trailer #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'container',
      headerName: 'Container #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'materials',
      headerName: 'Material',
      flex: 1,
      renderCell: params => {
        const { materialTypeCommonNames } = params.row
        return (
          <Tooltip content={materialTypeCommonNames?.join(', ')}>
            <>{materialTypeCommonNames?.length > 1 ? 'Mixed' : materialTypeCommonNames?.[0] || ''}</>
          </Tooltip>
        )
      },
      sortable: false,
    },
    {
      field: 'netWeight',
      headerName: 'Net Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: params => formatNumber(`${params?.row?.netWeight} ${defaultYardUm}`),
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'grossWeight',
      headerName: 'Gross Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: params => formatNumber(`${params?.row?.grossWeight} ${defaultYardUm}`),
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'tareWeight',
      headerName: 'Tare Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: params => formatNumber(`${params?.row?.tareWeight} ${defaultYardUm}`),
      sortable: !useBackendSortAndFilter,
    },
  ]
  if (useMinimalObt) {
    return columns.filter(
      column => column.field !== 'netWeight' && column.field !== 'salesOrder' && column.field !== 'container',
    )
  }
  return columns.filter(
    column => column.field !== 'grossWeight' && column.field !== 'tareWeight' && column.field !== 'ticketShipDate',
  )
}

const OutboundTicketGrid = props => {
  const classes = useStyles()
  const navigate = useNavigate()
  const handleRowClick = row => {
    navigate(`/app/sales/outbound-tickets/${row.id}`)
  }
  const { useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { defaultYardUm } = useSelector(state => state.settings)

  const { filter, setFilter, useBackendSortAndFilter } = props
  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter])
  const columns = getColumns(
    defaultYardTimezone,
    defaultYardUm,
    useMinimalObt,
    useBackendSortAndFilter,
    renderHeaderWithSort,
  )

  return <DataGridWithSearch onRowClick={handleRowClick} className={classes.root} {...props} columns={columns} />
}

OutboundTicketGrid.propTypes = DataGridWithSearch.propTypes

OutboundTicketGrid.defaultProps = DataGridWithSearch.defaultProps

export default OutboundTicketGrid
